import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { adminService, fetchAllPages } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { Badge, Button, Card, EmptyState, Spinner, StatCard, Pagination } from '../../components/ui';
import { getApiErrorMessage } from '../../utils/apiErrors';

const FILTERS = ['pending', 'approved', 'rejected', 'all'];
const PAGE_SIZE = 20;

function displayName(user, fallback) {
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
  return name || user?.email || fallback;
}

function AdminParentLinks() {
  const { t, i18n } = useTranslation();
  const { addNotification } = useNotification();
  const confirm = useConfirm();
  const [links, setLinks] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [page, setPage] = useState(1);

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const records = await fetchAllPages('/users/parent-links/');
      setLinks(records);
    } catch (error) {
      addNotification(t('Failed to load parent link requests'), 'error');
    } finally {
      setLoading(false);
    }
  }, [addNotification, t]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const counts = useMemo(() => links.reduce((result, link) => {
    result[link.status] = (result[link.status] || 0) + 1;
    return result;
  }, {}), [links]);

  const visibleLinks = filter === 'all' ? links : links.filter((link) => link.status === filter);
  const pagedLinks = visibleLinks.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const decide = async (link, decision) => {
    const parent = displayName(link.parent, t('Parent'));
    const student = displayName(link.student, t('Student'));
    const message = decision === 'approve'
      ? t('Approve the link between {{parent}} and {{student}}?', { parent, student })
      : t('Reject the link between {{parent}} and {{student}}?', { parent, student });
    if (!(await confirm(message))) return;

    setBusy(`${link.id}:${decision}`);
    try {
      const response = decision === 'approve'
        ? await adminService.approveParentLink(link.id)
        : await adminService.rejectParentLink(link.id);
      setLinks((current) => current.map((item) => (item.id === link.id ? response.data : item)));
      addNotification(decision === 'approve' ? t('Parent link approved') : t('Parent link rejected'), 'success');
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, 'Action failed'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const filterLabels = {
    pending: t('Pending'),
    approved: t('Approved'),
    rejected: t('Rejected'),
    all: t('All'),
  };

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Parent Links')} subtitle={t('Review connections requested between parents and students')} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <StatCard label={t('Pending')} value={counts.pending || 0} color="yellow" />
            <StatCard label={t('Approved')} value={counts.approved || 0} color="green" />
            <StatCard label={t('Rejected')} value={counts.rejected || 0} color="red" />
            <StatCard label={t('All Requests')} value={links.length} color="blue" />
          </div>

          <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
            {FILTERS.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => { setFilter(status); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${filter === status ? 'bg-primary-600 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
              >
                {filterLabels[status]} ({status === 'all' ? links.length : counts[status] || 0})
              </button>
            ))}
          </div>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : visibleLinks.length === 0 ? (
            <Card><EmptyState title={t('No parent links found')} description={t('No parent link requests match this filter.')} /></Card>
          ) : (
            <div className="space-y-3">
              {pagedLinks.map((link) => (
                <Card key={link.id} className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="grid sm:grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-5 min-w-0 flex-1">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">{t('Parent')}</p>
                      <p className="font-semibold text-gray-900 truncate">{displayName(link.parent, t('Parent'))}</p>
                      <p className="text-sm text-gray-500 truncate">{link.parent?.email}</p>
                    </div>
                    <div className="hidden sm:flex w-9 h-9 rounded-full bg-primary-50 text-primary-600 items-center justify-center" aria-hidden="true">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h11m0 0l-4-4m4 4l-4 4m1 6H5m0 0l4 4m-4-4l4-4" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">{t('Student')}</p>
                      <p className="font-semibold text-gray-900 truncate">{displayName(link.student, t('Student'))}</p>
                      <p className="text-sm text-gray-500 truncate">{link.student?.email}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap lg:justify-end items-center gap-2 lg:min-w-[285px]">
                    {link.relationship && <Badge tone="purple">{t(link.relationship)}</Badge>}
                    <Badge status={link.status}>{filterLabels[link.status] || link.status}</Badge>
                    <span className="text-xs text-gray-400 w-full lg:w-auto lg:mx-1">
                      {new Date(link.created_at).toLocaleDateString(i18n.language)}
                    </span>
                    {link.status === 'pending' && (
                      <>
                        <Button size="sm" variant="success" loading={busy === `${link.id}:approve`} disabled={Boolean(busy)} onClick={() => decide(link, 'approve')}>
                          {t('Approve')}
                        </Button>
                        <Button size="sm" variant="danger" loading={busy === `${link.id}:reject`} disabled={Boolean(busy)} onClick={() => decide(link, 'reject')}>
                          {t('Reject')}
                        </Button>
                      </>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
          <Pagination page={page} count={visibleLinks.length} pageSize={PAGE_SIZE} onPageChange={setPage} className="mt-4 rounded-xl border bg-white" />
        </main>
      </div>
    </div>
  );
}

export default AdminParentLinks;
